import React, { FC, useCallback, useMemo, useState } from 'react';

import { useFormContext } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { ProductsSequenceProduct } from 'common/contracts';
import { EProductType } from 'constants/enums';
import { EToasterStatus, showToast } from 'utils/showToast';
import { v4 as uuidv4 } from 'uuid';
import { ProductEntry } from 'views/Offers/Popups/Form/hooks/usePopupProducts';

import Button from 'components/Button/Button';
import DialogModal from 'components/DialogModal/DialogModal';
import RichText from 'components/RichText/RichText';

import {
  buildEmptyWeek,
  REWARD_CALENDAR_DAYS_PER_WEEK,
  REWARD_CALENDAR_MAX_WEEKS,
  REWARD_CALENDAR_MIN_WEEKS,
  RewardCalendarDay,
  RewardCalendarFormFields
} from '../../RewardCalendarForm.consts';
import { DayCard } from '../DayCard';
import { DayProductsList } from '../DayProductsList';
import { WeekChip } from '../WeekChip';

const quantityOf = (p: ProductEntry): number => Number(p.quantity) || 0;

/** UI-only: scaffold row from opening day / continue — excluded from gift count */
const REWARD_ROW_OPEN_SEED_MARK = 'openSeed' as const;
/** UI-only: row added via Add Reward button */
const REWARD_ROW_EXPLICIT_MARK = 'explicit' as const;

type CalendarProductRow = ProductEntry & {
  rewardRowSource?: typeof REWARD_ROW_OPEN_SEED_MARK | typeof REWARD_ROW_EXPLICIT_MARK;
};

function giftIconCountedRewardRows(products: ProductEntry[]): number {
  return products.filter(
    (p) =>
      (p as CalendarProductRow).rewardRowSource !== REWARD_ROW_OPEN_SEED_MARK
  ).length;
}

function dayHasFilledReward(products: ProductEntry[]): boolean {
  return products.some((p) => !!p.productId && quantityOf(p) > 0);
}

function canAppendRewardRow(products: ProductEntry[]): boolean {
  if (products.length === 0) return true;
  const last = products[products.length - 1];
  return !!(last?.productId && quantityOf(last) > 0);
}

interface IConfigurationTabProps {
  getProducts: UseQueryResult;
  formatProductQuantity: UseMutationResult<
    unknown,
    unknown,
    { quantity: string; productType: EProductType },
    unknown
  >;
}

const ConfigurationTab: FC<IConfigurationTabProps> = ({
  getProducts,
  formatProductQuantity
}) => {
  const form = useFormContext<RewardCalendarFormFields>();
  const weeks = form.watch('weeks');
  const weeksError = form.formState.errors.weeks?.message;

  const [activeWeekIndex, setActiveWeekIndex] = useState(0);
  const [openDayIndex, setOpenDayIndex] = useState<number | null>(null);
  const [weekIndexToDelete, setWeekIndexToDelete] = useState<number | null>(
    null
  );

  const canAddWeek = weeks.length < REWARD_CALENDAR_MAX_WEEKS;
  const canRemoveWeek = weeks.length > REWARD_CALENDAR_MIN_WEEKS;

  const activeWeek = weeks[activeWeekIndex] ?? weeks[0];
  const activeWeekDays = activeWeek?.days ?? [];

  const writeWeeks = useCallback(
    (nextWeeks: RewardCalendarFormFields['weeks']): void => {
      form.setValue('weeks', nextWeeks, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [form]
  );

  const handleAddWeek = useCallback((): void => {
    if (!canAddWeek) return;
    const weekToValidate = weeks[weeks.length - 1];
    const everyDayHasProduct = weekToValidate.days.every((day) =>
      dayHasFilledReward(day.products)
    );
    if (!everyDayHasProduct) {
      showToast({
        message:
          'You must configure products for this week in order to add a new week.',
        status: EToasterStatus.ERROR
      });
      return;
    }
    const next = [...weeks, buildEmptyWeek()];
    writeWeeks(next);
    setActiveWeekIndex(next.length - 1);
  }, [canAddWeek, weeks, writeWeeks]);

  const handleRemoveWeek = useCallback(
    (weekIndex: number): void => {
      if (weeks.length <= REWARD_CALENDAR_MIN_WEEKS) return;
      if (weekIndex === 0) return;
      const next = weeks.filter((_, i) => i !== weekIndex);
      writeWeeks(next);
      if (activeWeekIndex >= next.length) {
        setActiveWeekIndex(next.length - 1);
      } else if (activeWeekIndex > weekIndex) {
        setActiveWeekIndex(activeWeekIndex - 1);
      }
      setOpenDayIndex(null);
    },
    [weeks, writeWeeks, activeWeekIndex]
  );

  const mutateDayProducts = useCallback(
    (
      weekIndex: number,
      dayIndex: number,
      mutator: (products: ProductEntry[]) => ProductEntry[]
    ): void => {
      const currentWeeks = form.getValues('weeks');
      const next = currentWeeks.map((week, wi) => {
        if (wi !== weekIndex) return week;
        return {
          ...week,
          days: week.days.map((day, di) => {
            if (di !== dayIndex) return day;
            return { ...day, products: mutator(day.products) };
          })
        };
      });
      writeWeeks(next);
    },
    [form, writeWeeks]
  );

  const buildDayCell = useCallback(
    (
      weekIndex: number,
      dayIndex: number,
      products: ProductEntry[],
      weekDays: RewardCalendarDay[]
    ): {
      handlers: {
        addProduct: (p: ProductsSequenceProduct) => void;
        updateProduct: (id: string, p: ProductsSequenceProduct) => void;
        deleteProduct: (id: string) => void;
      };
      rewardQuantityTotal: number;
      canAddNext: boolean;
      hasFilledReward: boolean;
      continueToNextDay: () => void;
    } => {
      const handlers = {
        addProduct: (product: ProductsSequenceProduct) =>
          mutateDayProducts(weekIndex, dayIndex, (list) => [
            ...list,
            { ...(product as CalendarProductRow), id: uuidv4() }
          ]),
        updateProduct: (id: string, product: ProductsSequenceProduct) =>
          mutateDayProducts(weekIndex, dayIndex, (list) =>
            list.map((p) => (p.id === id ? { ...p, ...product } : p))
          ),
        deleteProduct: (id: string) =>
          mutateDayProducts(weekIndex, dayIndex, (list) =>
            list.filter((p) => p.id !== id)
          )
      };

      const continueToNextDay = (): void => {
        const nextIndex = dayIndex + 1;
        const nextDay = weekDays[nextIndex];
        setOpenDayIndex(nextIndex);
        if (nextDay?.products.length === 0) {
          mutateDayProducts(weekIndex, nextIndex, (list) => [
            ...list,
            {
              ...({} as CalendarProductRow),
              id: uuidv4(),
              rewardRowSource: REWARD_ROW_OPEN_SEED_MARK
            }
          ]);
        }
      };

      return {
        handlers,
        rewardQuantityTotal: giftIconCountedRewardRows(products),
        canAddNext: canAppendRewardRow(products),
        hasFilledReward: dayHasFilledReward(products),
        continueToNextDay
      };
    },
    [mutateDayProducts]
  );

  const weekChips = useMemo(
    () =>
      weeks.map((week, index) => (
        <WeekChip
          key={week.id}
          label={`Week ${index + 1}`}
          isActive={index === activeWeekIndex}
          canRemove={canRemoveWeek && index !== 0}
          onSelect={() => {
            setActiveWeekIndex(index);
            setOpenDayIndex(null);
          }}
          onRemove={() => setWeekIndexToDelete(index)}
        />
      )),
    [weeks, activeWeekIndex, canRemoveWeek]
  );

  return (
    <div className="flex flex-col">
      <RichText
        title="Configuration"
        description="Set up the reward calendar using weeks. Each week contains 7 days where rewards can be configured."
        className="mb-5"
      />
      {typeof weeksError === 'string' && weeksError.length > 0 && (
        <p className="mb-4 text-p-s text-destructive" role="alert">
          {weeksError}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 py-5 border-y border-base-sidebarBorder">
        {weekChips}
        {canAddWeek && (
          <button
            type="button"
            onClick={handleAddWeek}
            className="inline-flex items-center justify-center gap-2 flex-shrink-0 w-[83px] h-8 py-2 px-3 rounded-md border border-dashed bg-base-background text-p-xs font-medium transition-colors border-base-input text-base-foreground hover:bg-base-accent"
          >
            <FontAwesomeIcon
              icon="fa-regular fa-plus"
              className="text-[10px]"
            />
            <span>Week</span>
          </button>
        )}
      </div>

      {activeWeek && (
        <div className="mt-6">
          <h4 className="text-p-m font-semibold text-base-foreground mb-4">
            Week {activeWeekIndex + 1}
          </h4>

          <div className="grid grid-cols-4 gap-3">
            {activeWeekDays.map((day, dayIndex) => {
              const {
                handlers,
                rewardQuantityTotal,
                canAddNext,
                hasFilledReward,
                continueToNextDay
              } = buildDayCell(
                activeWeekIndex,
                dayIndex,
                day.products,
                activeWeekDays
              );

              return (
                <DayCard
                  key={day.id}
                  dayLabel={`Day ${dayIndex + 1}`}
                  rewardCount={rewardQuantityTotal}
                  isOpen={openDayIndex === dayIndex}
                  onOpenChange={(open) => {
                    setOpenDayIndex(open ? dayIndex : null);
                    if (open && day.products.length === 0) {
                      handlers.addProduct({
                        rewardRowSource: REWARD_ROW_OPEN_SEED_MARK
                      } as ProductsSequenceProduct);
                    }
                  }}
                  footer={
                    <div className="flex flex-col gap-2 w-full items-stretch">
                      <Button
                        type="button"
                        variant="secondary"
                        className="self-stretch"
                        disabled={!canAddNext}
                        leftIcon={
                          <FontAwesomeIcon icon="fa-regular fa-circle-plus" />
                        }
                        onClick={() =>
                          handlers.addProduct({
                            rewardRowSource: REWARD_ROW_EXPLICIT_MARK
                          } as ProductsSequenceProduct)
                        }
                      >
                        Add Reward
                      </Button>
                      {hasFilledReward &&
                        dayIndex < REWARD_CALENDAR_DAYS_PER_WEEK - 1 && (
                        <Button
                          type="button"
                          variant="default"
                          className="flex h-10 shrink-0 items-center justify-center gap-2 self-stretch rounded-md px-4 py-2 bg-indigo-700 text-white hover:bg-indigo-800"
                          onClick={continueToNextDay}
                        >
                          Continue to the Next Day
                        </Button>
                      )}
                    </div>
                  }
                >
                  <div className="flex shrink-0 w-full items-center justify-between">
                    <div className="flex items-center gap-2 text-p-s">
                      <span className="font-semibold text-base-foreground">
                        Day {dayIndex + 1}
                      </span>
                      <span className="text-base-mutedForeground">
                        | Week {activeWeekIndex + 1}
                      </span>
                    </div>
                    <button
                      type="button"
                      aria-label="Close day rewards"
                      className="text-base-mutedForeground hover:text-base-foreground"
                      onClick={() => setOpenDayIndex(null)}
                    >
                      <FontAwesomeIcon icon="fa-regular fa-xmark" />
                    </button>
                  </div>
                  <div className="flex shrink-0 w-full items-center justify-between">
                    <span className="text-p-s font-semibold text-base-foreground">
                      Rewards
                    </span>
                    <span className="inline-flex items-center gap-1 text-base-mutedForeground text-p-s">
                      <FontAwesomeIcon icon="fa-regular fa-gift" />
                      <span>{rewardQuantityTotal}</span>
                    </span>
                  </div>
                  <div
                    className="max-h-[min(320px,calc(100dvh-13rem))] min-h-[38px] min-w-0 w-full shrink overflow-y-auto overscroll-contain"
                  >
                    <DayProductsList
                      products={getProducts}
                      formatProductQuantity={formatProductQuantity}
                      productsList={day.products}
                      addProduct={handlers.addProduct}
                      updateProduct={handlers.updateProduct}
                      deleteProduct={handlers.deleteProduct}
                    />
                  </div>
                </DayCard>
              );
            })}
          </div>
        </div>
      )}

      {weekIndexToDelete !== null && (
        <DialogModal
          isOpen
          dataTestId="RewardCalendar-DeleteWeekModal"
          title={`Delete Week ${weekIndexToDelete + 1}`}
          customDescriptionContent={
            <>
              <span className="block">Are you sure you want to delete this week?</span>
              <span className="block mt-1">
                This action can&apos;t be undone.
              </span>
            </>
          }
          modalActions={{
            onConfirm: {
              label: 'Delete',
              action: () => {
                handleRemoveWeek(weekIndexToDelete);
                setWeekIndexToDelete(null);
              }
            },
            onClose: {
              label: 'Cancel',
              action: () => setWeekIndexToDelete(null),
              variant: 'link'
            }
          }}
        />
      )}
    </div>
  );
};

export default ConfigurationTab;
